import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Panel, Table, Pill } from "@/components/common";

const LINE = ["#4F46E5", "#0EA5E9", "#16A34A", "#F59E0B", "#EF4444", "#A855F7", "#14B8A6", "#64748B"];

function Trend({ trend }) {
  if (!trend) return null;
  const data = trend.months.map((m, i) => ({ month: m, ...Object.fromEntries(trend.projects.map((p) => [p.code, p.points[i].rate])) }));
  const cell = (pt) => (pt.rate == null ? <span className="text-slate-300">—</span> : <Pill tone={tone(pt.rate)}>{pt.rate}% <span className="opacity-60">({pt.on_time}/{pt.total})</span></Pill>);
  return (
    <Panel title="Monthly on-time delivery trend — last 6 months" testid="delivery-trend">
      {!trend.projects.length ? <p className="text-sm text-slate-500" data-testid="delivery-trend-empty">No deliveries logged in the last 6 months yet.</p> : (
        <>
          <div className="h-64" data-testid="delivery-trend-chart">
            <ResponsiveContainer>
              <LineChart data={data} margin={{ left: 0, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} width={44} />
                <Tooltip formatter={(v) => (v == null ? "no deliveries" : `${v}%`)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {trend.projects.map((p, i) => <Line key={p.id} type="monotone" dataKey={p.code} stroke={LINE[i % LINE.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 overflow-x-auto">
            <Table rows={trend.projects} cols={[{ k: "code", h: "Project", r: (p) => <Link to={`/projects/${p.id}?tab=deliveries`} className="text-indigo-700">{p.code}</Link> },
              ...trend.months.map((m, i) => ({ k: m, h: m, r: (p) => <span data-testid={`trend-${p.code}-${m}`}>{cell(p.points[i])}</span> }))]} />
          </div>
          <p className="mt-2 text-xs text-slate-500">Each month counts documents delivered to Basecamp that month: on time ÷ all delivered.</p>
        </>
      )}
    </Panel>
  );
}

const tone = (r) => (r == null ? "slate" : r >= 90 ? "green" : r >= 70 ? "amber" : "red");
const rateCol = (prefix) => ({ k: "rate", h: "On-time rate", r: (x) => <span data-testid={`${prefix}-rate-${x.code || x.id}`}><Pill tone={tone(x.rate)}>{x.rate == null ? "—" : `${x.rate}%`}</Pill></span> });
const counts = [{ k: "on_time", h: "On time" }, { k: "late", h: "Late" }, { k: "overdue", h: "Overdue now", r: (x) => (x.overdue ? <b className="text-red-600">{x.overdue}</b> : 0) }];

export function DeliveryScorecard({ sc, trend }) {
  if (!sc) return null;
  return (
    <>
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="Delivery scorecard — projects" testid="scorecard-projects">
        <p className="mb-2 text-xs text-slate-500">On-time rate = documents delivered to Basecamp by their due date ÷ (delivered + currently overdue). Lowest first.</p>
        <Table rows={sc.projects} empty="No deliveries logged or overdue yet" cols={[{ k: "code", h: "Project", r: (x) => <Link to={`/projects/${x.id}?tab=deliveries`} className="text-indigo-700">{x.code} {x.name}</Link> }, { k: "pm_name", h: "PM" }, ...counts, rateCol("sc-project")]} />
      </Panel>
      <Panel title="Delivery scorecard — people responsible" testid="scorecard-people">
        <p className="mb-2 text-xs text-slate-500">Counted against the person responsible for each document. Lowest first.</p>
        <Table rows={sc.people} empty="No deliveries logged or overdue yet" cols={[{ k: "name", h: "Person" }, ...counts, rateCol("sc-person")]} />
      </Panel>
    </div>
    <Trend trend={trend} />
    </>
  );
}
